import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import '../features/groups/groups.css';
import type { ActionResponse, DirectoryUserDto } from '../api/types';

function PeopleField({
  label,
  hint,
  people,
  onAdd,
  onRemove,
}: {
  label: string;
  hint?: string;
  people: DirectoryUserDto[];
  onAdd: (user: DirectoryUserDto) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      <UserPicker onSelect={(u) => u && onAdd(u)} placeholder="Person suchen und hinzufügen…" clearOnSelect />
      {people.length ? (
        <div className="grp-chips">
          {people.map((u) => (
            <span className="grp-chip" key={u.id}>
              <span className="pii">{u.displayName}</span>
              <button type="button" aria-label={`${u.displayName} entfernen`} onClick={() => onRemove(u.id)}>
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </Field>
  );
}

export function GroupsCreatePage() {
  const api = useApi();
  const prefixes = useAsync(() => api.getNamingPrefixes(), [api]);
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'groups.create')?.ticketMode ?? 'None';

  const [prefixId, setPrefixId] = useState<string | undefined>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isM365, setIsM365] = useState(false);
  const [owners, setOwners] = useState<DirectoryUserDto[]>([]);
  const [members, setMembers] = useState<DirectoryUserDto[]>([]);
  const [ticket, setTicket] = useState('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ActionResponse | null>(null);

  // Präfixe der gewählten Gruppen-Art (Sicherheit vs. Microsoft 365).
  const wantedScope = isM365 ? 'M365Group' : 'SecurityGroup';
  const groupPrefixes = useMemo(
    () => (prefixes.data ?? []).filter((p) => p.scope === wantedScope && p.enabled),
    [prefixes.data, wantedScope],
  );

  // Beim Wechsel der Gruppen-Art automatisch ein passendes Präfix vorwählen.
  useEffect(() => {
    if (!groupPrefixes.some((p) => p.id === prefixId)) {
      setPrefixId(groupPrefixes[0]?.id);
    }
  }, [groupPrefixes, prefixId]);

  const prefix = groupPrefixes.find((p) => p.id === prefixId);
  const finalName = `${prefix?.value ?? ''}${name.trim()}`;

  const addUnique = (list: DirectoryUserDto[], u: DirectoryUserDto) =>
    list.some((x) => x.id === u.id) ? list : [...list, u];

  function reset() {
    setName('');
    setDescription('');
    setTicket('');
    setOwners([]);
    setMembers([]);
    setDone(null);
    setError(null);
    setNameError(undefined);
    setTicketError(undefined);
  }

  async function submit() {
    if (!name.trim()) {
      setNameError('Ein Gruppenname ist erforderlich.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setNameError(undefined);
    setTicketError(undefined);
    setBusy(true);
    setError(null);
    try {
      const res = await api.createGroup({
        displayName: name.trim(),
        description: description.trim() || undefined,
        prefixId: groupPrefixes.length ? prefixId : undefined,
        isM365,
        ownerIds: owners.map((o) => o.id),
        memberIds: members.map((m) => m.id),
        ticketNumber: ticket.trim() || undefined,
      });
      setDone(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div>
        <PageHeader title="Gruppe erstellen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          {done.status === 'PendingApproval' ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
                Die Anlage ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="success">
              <MessageBarBody>
                <MessageBarTitle>Erledigt</MessageBarTitle>
                {done.message}
              </MessageBarBody>
            </MessageBar>
          )}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weitere Gruppe
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Gruppe erstellen"
        description="Neue Gruppe mit vorgegebener Namenskonvention (Governance) anlegen."
      />
      <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        <Field label="Gruppentyp">
          <RadioGroup layout="horizontal" value={isM365 ? 'm365' : 'security'} onChange={(_, d) => setIsM365(d.value === 'm365')}>
            <Radio value="security" label="Sicherheitsgruppe" />
            <Radio value="m365" label="Microsoft 365" />
          </RadioGroup>
        </Field>

        {groupPrefixes.length ? (
          <Field label="Namens-Präfix" hint="Vorgegeben durch die Governance.">
            <Dropdown
              value={prefix ? `${prefix.value}${prefix.description ? ` (${prefix.description})` : ''}` : ''}
              selectedOptions={prefixId ? [prefixId] : []}
              onOptionSelect={(_, d) => d.optionValue && setPrefixId(d.optionValue)}
            >
              {groupPrefixes.map((p) => (
                <Option key={p.id} value={p.id} text={`${p.value}${p.description ? ` (${p.description})` : ''}`}>
                  {p.value}
                  {p.description ? <i style={{ color: tokens.colorNeutralForeground3 }}> ({p.description})</i> : null}
                </Option>
              ))}
            </Dropdown>
          </Field>
        ) : null}

        <Field label="Name" required validationMessage={nameError}>
          <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="z. B. Vertrieb DACH" />
        </Field>

        <Field label="Beschreibung">
          <Textarea value={description} onChange={(_, d) => setDescription(d.value)} rows={2} placeholder="Optionaler Zweck der Gruppe" />
        </Field>

        <PeopleField
          label="Owner"
          hint="Optional — verantwortlich für die Gruppe."
          people={owners}
          onAdd={(u) => setOwners((p) => addUnique(p, u))}
          onRemove={(id) => setOwners((p) => p.filter((x) => x.id !== id))}
        />
        <PeopleField
          label="Mitglieder"
          hint="Optional — direkt bei der Anlage hinzufügen."
          people={members}
          onAdd={(u) => setMembers((p) => addUnique(p, u))}
          onRemove={(id) => setMembers((p) => p.filter((x) => x.id !== id))}
        />

        {name.trim() ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacingHorizontalS,
              padding: tokens.spacingVerticalS + ' ' + tokens.spacingHorizontalM,
              borderRadius: tokens.borderRadiusLarge,
              backgroundColor: tokens.colorNeutralBackground2,
            }}
          >
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Vollständiger Name:</Caption1>
            <Text weight="semibold">{finalName}</Text>
            <Badge appearance="tint" color={isM365 ? 'brand' : 'informative'}>
              {isM365 ? 'Microsoft 365' : 'Sicherheit'}
            </Badge>
          </div>
        ) : null}

        {ticketMode !== 'None' ? (
          <Field
            label="Ticketnummer"
            required={ticketMode === 'Required'}
            validationMessage={ticketError}
            hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
          >
            <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" />
          </Field>
        ) : null}

        <div>
          <Button appearance="primary" disabled={!name.trim() || busy} onClick={submit}>
            {busy ? 'Bitte warten…' : 'Gruppe anlegen'}
          </Button>
        </div>
      </div>
    </div>
  );
}
